<template>
  <div class="pin-container">
    <div id="customisable-lock-screen" class="customisable-lock-screen" data-position="0" data-name="个性锁屏" ref="carouselContainer">
      <div class="lock-screen-text-container" v-animate>
        <p class="lock-screen-title ffm500">个性锁屏</p>
        <p class="lock-screen-sub-title ffm600">时光，由你定格</p>
        <div class="lock-screen-description-container ffm400">
          <p>我们将「华容网格」的巧思融入锁屏设计。</p>
          <p v-if="isPC">现在，锁屏时钟可以随照片布局自定义调整，还能让数字随意伸展。</p>
          <template v-else>
            <p>现在，锁屏时钟可以随照片布局自定义调整，</p>
            <p>还能让数字随意伸展。</p>
          </template>
        </div>
      </div>
      <div class="carousel-container">
        <div class="carousel-scene">
          <div class="carousel-ring" ref="carouselRing">
            <div
              v-for="(i) in 14"
              :key="i"
              :ref="`slide-${i}`"
              class="carousel-slide"
              @click="goToSlide(i - 1)"
            >
              <PlusImage class="slide-content" :src="CUSTOMISABLE_LOCK_SCREEN[`wallpaper${i}`]" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { gsap, ScrollTrigger } from "@/utils/gsapConfig.js";
import { initExposureTrack } from "@/utils/track.js";
import PlusImage from "@/components/PlusImage.vue";
import { CUSTOMISABLE_LOCK_SCREEN } from '@/config/image'; 

// 透明占位符（1x1 gif），用于释放大图内存时占位，避免<img>塌陷闪动
const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAAAAACw=';

export default {
  name: "CustomisableLockScreen",
  components: {
    PlusImage
  },
  props: {
  },
  data() {
    return {
      CUSTOMISABLE_LOCK_SCREEN,
      angleStep: 180 / 7,
      autoplayTimer: null,
      scrollTriggerInstance: null,
      updateThrottleTimer: null,
      lastUpdateTime: 0,
      // hasplayedOnce: false,
    };
  },
  mounted() {
    initExposureTrack('.customisable-lock-screen')()
    this.initCarousel()
    if (this.isPC) {
      this.initScrollTrigger()
    }
    let lastWidth = window.innerWidth;
    this.resizeHandler = () => {
      this.initCarousel()
      if (this.isPC) {
        if (Math.abs(window.innerWidth - lastWidth) > 10) {
          lastWidth = window.innerWidth;
          this.initScrollTrigger()
        }
      }
    }
    window.addEventListener('resize', this.resizeHandler)
  },
  methods: {
    initCarousel() {
      const ring = this.$refs.carouselRing
      const slides = this.$el.querySelectorAll('.carousel-slide')
      const viewportWidth = window.innerWidth
      const radius = Math.round((1787 / 2) * (viewportWidth / 1920))

      gsap.timeline()
        .set(ring, { rotationY: 0 })
        .set(slides, {
          rotateY: (i) => i * -this.angleStep,
          transformOrigin: `50% 50% ${radius}px`,
          z: -radius, // z轴位置为负半径
          backfaceVisibility: 'hidden',
          force3D: true, // 强制使用3D加速
          webkitBackfaceVisibility: 'hidden',
          webkitTransformStyle: 'preserve-3d'
        })
      
      // 缓存原始图片 src 信息，便于后续按需加载/卸载
      slides.forEach(slide => {
        const img = slide.querySelector('img')
        if (img && !img.dataset.originalSrc) {
          img.dataset.originalSrc = img.getAttribute('src') || ''
        }
        img.setAttribute('src', TRANSPARENT_PIXEL)
        img.style.visibility = 'hidden'
      })

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const img = entry.target.querySelector('img')
          if (entry.isIntersecting) {
            img.src = img.dataset.originalSrc
            img.style.visibility = 'visible'
          } else {
            img.src = TRANSPARENT_PIXEL
            img.style.visibility = 'hidden'
          }
        })
      }, { threshold: 0.01 })

      slides.forEach(slide => observer.observe(slide))
      this.applyIOSBackfaceFix(slides)
      
      this.updateSlideStates(true)
    },

    initScrollTrigger() {
    if (!this.isPC) return
      
      const container = this.$refs.carouselContainer
      if (!container) return

      if (this.scrollTriggerInstance) {
        this.scrollTriggerInstance.kill()
      }

      this.scrollTriggerInstance = ScrollTrigger.create({
        // markers: true,
        trigger: container,
        start: () => {
          const offset = 24 / 1920 * window.innerWidth;
            return `top top+=${offset}`;
        },
        end: "+=" + container.offsetHeight,
        pin: true,
        anticipatePin: 1,
        onEnter: (self) => {
          if (self.direction === 1) {
            this.startAutoplay()
          }
        },
        onLeave: () => {
          this.stopAutoplay()
          this.snapToNearestSlide()
        },
        onEnterBack: (self) => {
          if (self.direction === 1) {
            this.startAutoplay()
          }
        },
        onLeaveBack: () => {
          this.stopAutoplay()
          this.snapToNearestSlide()
        }
      })
    },
    
    updateSlideStates(force = false) {
      const slides = this.$el.querySelectorAll('.carousel-slide')
      const ring = this.$refs.carouselRing
      const currentRingRotation = gsap.getProperty(ring, 'rotationY') || 0
      
      slides.forEach((slide, index) => {
        const slideBaseAngle = index * -this.angleStep
        const effectiveAngle = slideBaseAngle + currentRingRotation
        const normalizedAngle = ((effectiveAngle % 360) + 360) % 360
        const distanceFromFront = Math.min(normalizedAngle, 360 - normalizedAngle)
        
        let zIndex = 1
        if (distanceFromFront <= 12.85) zIndex = 10
        else if (distanceFromFront <= 38.55) zIndex = 8
        else if (distanceFromFront <= 64.25) zIndex = 6
        else if (distanceFromFront <= 90) zIndex = 4
        else if (distanceFromFront <= 115.7) zIndex = 2
        
        const img = slide.querySelector('img')
        if (img) {
          // 仅在需要变更时执行，避免反复 setAttribute 造成闪烁
            if (distanceFromFront > 140) {
              if (img.dataset.originalSrc && img.getAttribute('src') !== TRANSPARENT_PIXEL) {
                img.setAttribute('data-current-loaded', '0')
                img.setAttribute('src', TRANSPARENT_PIXEL)
                img.style.visibility = 'hidden'
              }
            } else if (distanceFromFront <= 130) {
              if (img.dataset.originalSrc && img.getAttribute('src') === TRANSPARENT_PIXEL) {
                img.setAttribute('src', img.dataset.originalSrc)
              }
              img.style.visibility = 'visible'
            } else {
              // 中间缓冲区
            }
        }
        
        gsap.set(slide, { zIndex, force3D: true })
      })
    },
    
    snapToNearestSlide() {
      const ring = this.$refs.carouselRing
      if (!ring) return
      
      const currentRotation = gsap.getProperty(ring, 'rotationY') || 0
      
      // 计算最近的吸附位置
      const nearestSlideIndex = Math.round(currentRotation / this.angleStep)
      const targetRotation = nearestSlideIndex * this.angleStep
      
      // 平滑过渡到最近的吸附位置
      gsap.to(ring, {
        rotationY: targetRotation,
        duration: 0.6,
        ease: 'power2.out',
        onUpdate: () => {
          this.updateSlideStates()
        }
      })
    },
    
    goToSlide(index) {
      this.stopAutoplay()
      const ring = this.$refs.carouselRing
      const currentRotation = gsap.getProperty(ring, 'rotationY') || 0
      const targetRotation = index * this.angleStep
      
      let rotationDiff = targetRotation - currentRotation
      
      while (rotationDiff > 180) {
        rotationDiff -= 360
      }
      while (rotationDiff < -180) {
        rotationDiff += 360
      }
      
      const finalRotation = currentRotation + rotationDiff
      
      gsap.to(ring, {
        rotationY: finalRotation,
        duration: 1.0,
        ease: 'power1.inOut',
        onUpdate: () => {
          this.updateSlideStates()
        }
      })
    },
    
    startAutoplay() {
      if (!this.isPC) return
      // if (this.hasPlayedOnce) return
      // this.hasPlayedOnce = true
      if (this.autoplayTimer) return
      
      const carouselRing = this.$refs.carouselRing
      if (!carouselRing) return
      
      const fullRotation = 180
      const rotationDuration = 4.5
      
      this.autoplayTimer = gsap.to(carouselRing, {
        rotationY: `+=${fullRotation}`,
        duration: rotationDuration,
        ease: 'power1.inOut',
        repeat: 0, 
        onUpdate: () => {
          this.updateSlideStates()
        }
      })
    },
    stopAutoplay() {
      if (this.autoplayTimer) {
        this.autoplayTimer.kill()
        this.autoplayTimer = null
      }
    },

    applyIOSBackfaceFix(slides) {
      const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && 
                          /Safari/.test(navigator.userAgent) && 
                          !/Chrome/.test(navigator.userAgent)
      
      if (isIOSSafari) {
        slides.forEach(slide => {
          const img = slide.querySelector('img')
          if (img) {
            // 强制应用backface-visibility样式
            img.style.webkitBackfaceVisibility = 'hidden'
            img.style.backfaceVisibility = 'hidden'
            img.style.webkitTransformStyle = 'preserve-3d'
            img.style.transformStyle = 'preserve-3d'
          }
          
          slide.style.webkitBackfaceVisibility = 'hidden'
          slide.style.backfaceVisibility = 'hidden'
          slide.style.webkitTransformStyle = 'preserve-3d'
          slide.style.transformStyle = 'preserve-3d'
        })
      }
    }
  },
  computed: {
    ...mapGetters(['isMobile', 'isPad', 'isPC']),
  }
};
</script>

<style lang="scss" scoped>
.customisable-lock-screen {
  height: calc(900 + 120 * 2) * $px1920vw + vw;
  padding: 120 * $px1920vw + vw 0;
  background: rgba(0, 0, 0, 1);
  position: relative;
}

.customisable-lock-screen {
  display: flex;
  flex-direction: column;
  align-items: center;

  .lock-screen-text-container{
    position: relative;
    z-index: 1;
    .lock-screen-title{
      /* H4_28号/副标题(pc) */
      font-size: 28 * $px1920vw + vw;
      line-height: 33.6 * $px1920vw + vw;
      text-align: center;
      letter-spacing: normal;
      color: #FFFFFF;
    }

    .lock-screen-sub-title{
      /* H1_64号/一级标题(pc) */
      font-size: 64 * $px1920vw + vw;
      line-height: 76.8 * $px1920vw + vw;
      text-align: center;
      letter-spacing: normal;
      color: #FFFFFF;
      margin-top: 12 * $px1920vw + vw;
    }

    .lock-screen-description-container{
      /* B_18号/正文(pc) */
      font-size: 18 * $px1920vw + vw;
      line-height: 25.2 * $px1920vw + vw;
      text-align: center;
      letter-spacing: normal;
      color: rgba(255, 255, 255, 0.8);
      margin-top: 24 * $px1920vw + vw;
    }
  }
  .carousel-container {
    width: 2023 * $px1920vw + vw;
    height: 948.55 * $px1920vw + vw;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    backface-visibility: hidden;
    top: 170 * $px1920vw + vw;
    position: absolute;
    background: rgba(0, 0, 0, 1);
  }
  
  .carousel-scene {
    perspective: 1300 * $px1920vw + vw;
    -webkit-perspective: 1300 * $px1920vw + vw;
    position: relative;
    width: 1800 * $px1920vw + vw;
    height: 614 * 1.2 * $px1920vw + vw;
    // margin-top: 101 * $px1920vw + vw;
    overflow: hidden;
  }
  
  .carousel-ring {
    position: relative;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
    will-change: transform;
    pointer-events: none;
  }
  
  .carousel-slide {
    position: absolute;
    width: 246 * 1.35 * $px1920vw + vw;
    height: auto;
    // height: 614 * 1.2 * $px1920vw + vw;
    left: 50%;
    // top: 50%;
    margin-left: -123 * 1.35 * $px1920vw + vw;
    // margin-top: -307 * $px1920vw + vw;
    transform-style: preserve-3d;
    cursor: pointer;
    will-change: transform, opacity, z-index;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    pointer-events: auto;
    -webkit-transform-style: preserve-3d;
  }
  
  .slide-content {
    width: 100%;
    height: 100%;
    position: relative;
    transform-style: preserve-3d;
    will-change: transform;
    object-fit: cover;
    pointer-events: auto;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    -webkit-transform-style: preserve-3d;
    
    :deep(img) {
      backface-visibility: hidden;
      -webkit-backface-visibility: hidden;
      transform-style: preserve-3d;
      -webkit-transform-style: preserve-3d;
    }
  }
}

.pin-container {
  background: rgba(0, 0, 0, 1);
}


// pad端
.isPad {
  .customisable-lock-screen{
    height: 460 * $px676vw + vw;
    padding-top: 36 * $px676vw + vw;

    .lock-screen-text-container{
      width: 286 * $px676vw + vw;
      .lock-screen-title{
        font-size: 12 * $px676vw + vw;
        line-height: normal;
      }

      .lock-screen-sub-title{
        font-size: 25 * $px676vw + vw;
        line-height: normal;
        margin-top: 6 * $px676vw + vw;
      }

      .lock-screen-description-container{
        font-size: 12 * $px676vw + vw;
        line-height: 16 * $px676vw + vw;
        margin-top: 12 * $px676vw + vw;
      }
    }
    .carousel-container {
      width: 704 * $px676vw + vw;
      height: 330 * $px676vw + vw;
      top: 139 * $px676vw + vw;
    }
    .carousel-scene {
      perspective: 700 * $px676vw + vw;
    }
  }
}

// 手机端
.isMobile {
  .customisable-lock-screen{
    height: 460 * $px360vw + vw;
    padding-top: 36 * $px360vw + vw;

    .lock-screen-text-container{
      width: 286 * $px360vw + vw;
      .lock-screen-title{
        font-size: 12 * $px360vw + vw;
        line-height: normal;
      }

      .lock-screen-sub-title{
        font-size: 25 * $px360vw + vw;
        line-height: normal;
        margin-top: 6 * $px360vw + vw;
      }

      .lock-screen-description-container{
        font-size: 12 * $px360vw + vw;
        line-height: 16 * $px360vw + vw;
        margin-top: 12 * $px360vw + vw;
      }
    }
    .carousel-container {
      width: 767.78 * $px360vw + vw;
      height: 360 * $px360vw + vw;
      top: 121 * $px360vw + vw;
      transform: scale(1.5);
    }
    .carousel-scene {
      perspective: 600 * $px360vw + vw;
    }
  }
}
</style>